import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Typography, Scrollbar } from '@ann/web-components';
import { ChatInput } from './components';
import UCPMessage from './components/UCPMessage';
import { useChatContext } from '../../../../context/ChatContext';
import { useChatCommunication } from '../../../../hooks/useChatCommunication';
import { useStore, useWebSocketScope } from '../../../../../../../hooks';
import { WebSocketScope } from '../../../../../../../utils/enums';
import dayjs from 'dayjs';
import {
  ChatPanelContainer,
  MessagesContainer,
  MessageBubble,
  MessageContainer,
  InputContainer,
  ChatPanelContent,
} from './ChatPanel.styled';

const AGENT_ID = process.env.REACT_APP_UCP_AGENT_UUID;

const getPayloadConversationId = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return (
    payload.conversationUuid ??
    payload.conversation_uuid ??
    payload.context?.conversation_id ??
    payload.context?.conversationUuid ??
    null
  );
};

const ORCHESTRATOR_NO_CONTENT_EVENTS = new Set([
  'chat.response.created',
  'chat.response.output_item.added',
  'chat.response.in_progress',
  'chat.response.writing',
  'chat.response.output_item.done',
  'chat.response.finalizing',
  'chat.response.sources.final',
]);

const ChatPanel = ({ contactId }) => {
  const { authStore } = useStore();
  const chatContext = useChatContext();
  const { sendMessage, isConnected } = useChatCommunication();
  
  const { conversation } = chatContext;
  const { messages, agentIsTyping, conversationID } = conversation;
  
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  const streamingChunksRef = useRef({
    content: '',
    messageId: null,
  });
  
  const expectedConversationRef = useRef(null);
  const orchestratorStreamTextRef = useRef('');
  const assistantReplyDeliveredRef = useRef(false);

  useEffect(() => {
    if (bottomRef.current) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }, 100);
    }
  }, [messages]);

  const prevContactIdRef = useRef(contactId);
  useEffect(() => {
    if (prevContactIdRef.current !== contactId) {
      chatContext.clearConversation();
      streamingChunksRef.current = { chunks: {}, messageId: null, totalReceived: 0, isComplete: false };
      expectedConversationRef.current = null;
      orchestratorStreamTextRef.current = '';
      assistantReplyDeliveredRef.current = false;
      prevContactIdRef.current = contactId;
    }
  }, [contactId, chatContext]);

  const finalizeAssistantReply = useCallback(
    (text, source) => {
      const trimmed = text != null ? String(text).trim() : '';
      if (!trimmed) return;
      chatContext.setMessages((prev) => [
        ...prev,
        { message: trimmed, type: 'received', time: dayjs().format('HH:mm') },
      ]);
      chatContext.setAgentIsTyping(false);
      setIsLoading(false);
      assistantReplyDeliveredRef.current = true;
      orchestratorStreamTextRef.current = '';
    },
    [chatContext]
  );

  useWebSocketScope(WebSocketScope.CONVERSATION, useCallback((wsMessage) => {
    const payload = wsMessage.payload || {};
    const event = wsMessage.event;
    const payloadConvId = getPayloadConversationId(payload);
    const innerData = payload.data && typeof payload.data === 'object' ? payload.data : {};

    const currentConversationId = conversationID;
    const expectedConvId = expectedConversationRef.current;
    const validConversationId = currentConversationId || expectedConvId;
    
    if (validConversationId) {
      if (payloadConvId && payloadConvId !== validConversationId) {
        return;
      }
    } else {
      if (!agentIsTyping) {
        return;
      }
      if (payloadConvId) {
        expectedConversationRef.current = payloadConvId;
        chatContext.setConversationID(payloadConvId);
      }
    }

    // ─── Orchestrator (async chat) ─────────────────────────────────────
    if (event === 'chat.response.output_item.committed') {
      if (assistantReplyDeliveredRef.current) return;
      const item = innerData.item;
      const content = item?.content;
      if (typeof content === 'string' && content.trim()) {
        finalizeAssistantReply(content, 'chat.response.output_item.committed');
        return;
      }
    }

    if (event === 'chat.response.output_text.delta') {
      const deltaText = innerData.content;
      if (typeof deltaText === 'string') {
        orchestratorStreamTextRef.current += deltaText;
      }
      return;
    }

    if (event === 'chat.response.completed') {
      if (!assistantReplyDeliveredRef.current) {
        const buffered = orchestratorStreamTextRef.current;
        if (buffered && String(buffered).trim()) {
          finalizeAssistantReply(buffered, 'chat.response.completed(buffer)');
        } else {
          chatContext.setAgentIsTyping(false);
          setIsLoading(false);
        }
      } else {
        chatContext.setAgentIsTyping(false);
        setIsLoading(false);
      }
      orchestratorStreamTextRef.current = '';
      return;
    }

    if (ORCHESTRATOR_NO_CONTENT_EVENTS.has(event)) {
      return;
    }
    
    // ─── Legacy chunk-based streaming (DELTA/COMPLETE with chunkContent) ─
    if ((event === 'DELTA' || event === 'COMPLETE') && payload.chunkContent !== undefined) {
      if (assistantReplyDeliveredRef.current) {
        return;
      }
      if (!streamingChunksRef.current.chunks) {
        streamingChunksRef.current = { 
          chunks: {}, 
          messageId: null, 
          totalReceived: 0,
          isComplete: false 
        };
      }
      
      const chunkIndex = payload.chunkIndex;
      if (!streamingChunksRef.current.chunks[chunkIndex]) {
        streamingChunksRef.current.chunks[chunkIndex] = payload.chunkContent;
        streamingChunksRef.current.totalReceived++;
      }
      streamingChunksRef.current.messageId = payload.messageId;
      
      if (event === 'COMPLETE' || payload.isLastChunk) {
        streamingChunksRef.current.isComplete = true;
        streamingChunksRef.current.lastChunkIndex = chunkIndex;
      }
      
      const { chunks, isComplete, lastChunkIndex, totalReceived } = streamingChunksRef.current;
      const expectedCount = lastChunkIndex !== undefined ? lastChunkIndex + 1 : null;
      
      if (isComplete && expectedCount !== null && totalReceived >= expectedCount) {
        let completeMessage = '';
        for (let i = 0; i <= lastChunkIndex; i++) {
          completeMessage += chunks[i] || '';
        }
        
        chatContext.setMessages(prev => [...prev, {
          message: completeMessage,
          type: "received",
          time: dayjs().format('HH:mm'),
        }]);
        assistantReplyDeliveredRef.current = true;
        chatContext.setAgentIsTyping(false);
        setIsLoading(false);
        streamingChunksRef.current = { chunks: {}, messageId: null, totalReceived: 0, isComplete: false };
      }
      return;
    }

    
    // ─── Legacy flat text fallback ─────────────────────────────────────
    const responseText =
      innerData.message_text ||
      innerData.messageText ||
      innerData.message ||
      innerData.text ||
      innerData.response ||
      payload.message_text ||
      payload.messageText ||
      payload.message ||
      payload.text ||
      payload.response;
    
    if (responseText) {
      if (assistantReplyDeliveredRef.current) {
        return;
      }
      chatContext.setMessages(prev => [...prev, {
        message: responseText,
        type: "received",
        time: dayjs().format('HH:mm'),
      }]);
      assistantReplyDeliveredRef.current = true;
      chatContext.setAgentIsTyping(false);
      setIsLoading(false);
      
      const newConversationId =
        getPayloadConversationId(payload) ||
        payload.conversation_uuid ||
        payload.conversationUuid;
      if (newConversationId) {
        chatContext.setConversationID(newConversationId);
      }
    }
  }, [chatContext, conversationID, agentIsTyping, contactId, finalizeAssistantReply]));

  const handleSendMessage = useCallback(async (message, options = {}) => {
    if (!message.trim() || !isConnected) return;

    setIsLoading(true);
    chatContext.setIsSendMessageClicked(true);

    const userMessage = {
      message: message,
      type: "sent",
      time: dayjs().format('HH:mm')
    };

    const localMessages = [...messages, userMessage];
    chatContext.setMessages(localMessages);
    setNewMessage("");

    const params = {
      message_text: message,
      tenant_id: authStore.selectedAccount?.tenantId || "default-tenant",
      sender_uuid: authStore.selectedAccount?.id,
      assistant_uuid: AGENT_ID,
      conversation_uuid: conversationID || "",
      channel: "WEB",
      metadata: JSON.stringify({}),
      params: {
        party_uuid: contactId
      }
    };

    try {
      orchestratorStreamTextRef.current = '';
      assistantReplyDeliveredRef.current = false;
      chatContext.setAgentIsTyping(true);
      
      if (conversationID) {
        expectedConversationRef.current = conversationID;
      }

      const result = await sendMessage(params);
      
      if (result.success) {
        const newConvId = result.data?.conversation_uuid || result.data?.conversationUuid;
        if (newConvId) {
          expectedConversationRef.current = newConvId;
          chatContext.setConversationID(newConvId);
        }
      } else {
        chatContext.setAgentIsTyping(false);
        setIsLoading(false);
        console.error('Failed to send message:', result.error);
        
        chatContext.setMessages([...localMessages, {
          message: "Failed to send message. Please try again.",
          type: "received",
          time: dayjs().format('HH:mm'),
        }]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      chatContext.setAgentIsTyping(false);
      setIsLoading(false);
      
      chatContext.setMessages([...localMessages, {
        message: "Oops! Something went wrong. Please try again.",
        type: "received",
        time: dayjs().format('HH:mm'),
      }]);
    } finally {
      chatContext.setIsSendMessageClicked(false);
    }
  }, [messages, conversationID, isConnected, sendMessage, chatContext, authStore, contactId]);

  const handleMessageChange = useCallback((e) => {
    setNewMessage(e.target.value);
  }, []);

  const handleSend = useCallback((message) => {
    handleSendMessage(message);
  }, [handleSendMessage]);

  const handleCleanThread = useCallback(() => {
    orchestratorStreamTextRef.current = '';
    assistantReplyDeliveredRef.current = false;
    chatContext.clearConversation();
    setNewMessage('');
  }, [chatContext]);

  return (
    <ChatPanelContainer>
      <ChatPanelContent>
      <Scrollbar size="small">
        <MessagesContainer ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <MessageContainer>
              <MessageBubble $type="system">
                <Typography category="body" weight="regular" size="medium" color="body">
                  Hi there! How can I help you today?
                </Typography>
              </MessageBubble>
            </MessageContainer>
          ) : (
            messages.map((message, index) => (
              <UCPMessage
                key={`${message.type}-${index}-${message.time}`}
                messageItem={message}
                isTyping={false}
              />
            ))
          )}
          {agentIsTyping && (
            <UCPMessage
              messageItem={{ message: "Thinking...", type: "received", time: dayjs().format('HH:mm') }}
              isTyping={true}
            />
          )}
          <div ref={bottomRef} />
        </MessagesContainer>
      </Scrollbar>
      </ChatPanelContent>
      <InputContainer>
        <ChatInput
          value={newMessage}
          onChange={handleMessageChange}
          onSend={handleSend}
          placeholder="Type your instructions here..."
          disabled={isLoading || agentIsTyping || !isConnected}
        />
      </InputContainer>
    </ChatPanelContainer>
  );
};

export default ChatPanel;
